import React, {useEffect, useState} from "react";
import { withSidebar } from "../hoc/withSidebar";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import actions from "../../actions";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import {rejectTransactionsHeading} from "../../constants/RejectTransactionsConstant";
import moment from "moment";
import { listProps, formatDate, setFinancialInstitutionUIData, getComponentAccess } from "../../utils/helper";
import { RoleType }  from "../../constants/sharedConstants.js";
import ERPSRecallRejectSearch, { getDateLimits, billerDetails } from "../custom/ERPSRecallRejectSearch";
import {userActions} from "../../actions/user.actions";
import { ResponsiveBox, Typography } from "../primitives";

const RejectTransactions = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        history,
        location: { state, pathname },
    } = props;

    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(0);

    const user = useSelector((state) => state.loginUserReducer.user);
    const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);

    const componentAccess = state?.componentAccess
            || getComponentAccess( { function_type_code: "RPM", label_type_code: "TE" }, user );

    const rejectTransactionResponse = useSelector(
        (state) => state.transactionEnquiryReducer
    );

    const dates = getDateLimits();

    const initialSearchValues = {
        paymentFromDate: dates.aMonthAgo,
        paymentToDate: dates.today,
        transactionToAmount: "",
        verificationNumber: "",
        searchBy: billerDetails[0].value,   // "billerId"
        billerId: isBillerRole ? user.billerNonVolatileDto.biller_id : null, // internal biller id for Biller Role
        bmoBillerId: "",  // external biller id entered by the user
        billerName: "",
        billerCustomerNumber: "",
        fiCustomerName: "",
        selectedFiNumber: "0",
        sortBy: "ED",
      };

    const [searchValues, setSearchValues] = useState(initialSearchValues);

    const [actualCriteria, setActualCriteria] = useState(
        state?.fromDetailPage ? rejectTransactionResponse?.transactionForRejectDetails : { ...initialSearchValues, offset: 0, limit: 100}
    );

    const [transactionData, setTransactionData] = useState( state?.fromDetailPage && rejectTransactionResponse?.transactionForRejectData.data || null );

    const [fiList, setFiList] = useState([]);
    const [fiMap, setFiMap] = useState(null);
    const fiReducer = useSelector( (state) => state.fiReducer );
    const currentFiNumber = useSelector((state) => state.fiReducer?.selectedFi || user?.fiNumber);

    const [isSubscription] = useState([RoleType.ERPADMIN.type, RoleType.BILLADMIN.type].includes(user.role.role_type));

    const subscribeEntities = useSelector( (state) => state.subscribingEntitiesReducer );
    const [subscribeEntityList, setSubscribeEntityList] = useState([]);

    useEffect(() => {
        setFinancialInstitutionUIData(fiReducer?.financialInstitutionList, setFiMap, setFiList, null, user?.languageCode);
    }, [fiReducer.financialInstitutionList, user?.languageCode])

    useEffect(() => {
        if (isSubscription && subscribeEntities.subscribingEntitiesList?.length > 0) {
            const data = subscribeEntities.subscribingEntitiesList.map( se => ({value: se.fiCompanyNumber, label: se.fiCompanyName}) );
            data.unshift({value: "", label: ""});
            setSubscribeEntityList(data);
        }
    }, [subscribeEntities.subscribingEntitiesList]);

    useEffect(() => {
        if (!(fiReducer?.financialInstitutionList?.length > 0))
            dispatch(actions.fiActions.getFinancialInstitutionList(t));

        if (isSubscription)
            dispatch(userActions.getSubscribingEntitiesList(currentFiNumber));

        if (state?.fromDetailPage) {
            // transfer into searchValues the attributes saved in rejectTransactionResponse.transactionForRejectDetails
            Object.keys(searchValues).filter(key => rejectTransactionResponse.transactionForRejectDetails[key])
                  .forEach(key => searchValues[key] = rejectTransactionResponse.transactionForRejectDetails[key]);
            setPageNumber(actualCriteria.offset / actualCriteria.limit);
            state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
            if (state.isRejected)
                loadResults(actualCriteria, " (After Reject)");
        } else if (state?.searchedValues) {
            setSearchValues(state?.searchedValues);
        } else {  // reset search resutls previously saved in the reducer
            dispatch({
                type: "GET_TRANSACTION_FOR_REJECT_SUCCESS",
                data: { data: null, transactionForRejectDetails: null },
              });
        }
    }, []);

    useEffect(() => {
        if (rejectTransactionResponse) {
            const transactionForRejectRes = rejectTransactionResponse?.transactionForRejectData?.data;

            if (transactionForRejectRes?.length)
                transactionForRejectRes.forEach((r, idx) => r.seqNo = idx + 1 + actualCriteria.offset);
            setTransactionData(transactionForRejectRes);
        }
    }, [rejectTransactionResponse]);

    const paginate = (values) => {  // 'values' has fields startOffset and endOffset (e.g., for 1st Page: 0,25;  2nd Page: 25,50;  3rd: 50,75...)
        actualCriteria.offset = values.startOffset;
        if (actualCriteria.limit !== (values.endOffset - values.startOffset)) // if the user changed the number of rows per page
          actualCriteria.limit = values.endOffset - values.startOffset;
    
        loadResults(actualCriteria, " (paginate)");
    };

    const loadResults = (queryParams, prompt) => {
        if (errorMessage)
          setErrorMessage(null);
        setLoading(true);

        dispatch(
          actions.transactionEnquiryActions.getTransactionsForReject(
            queryParams,
            { ...props, t, history, setLoading, setErrorMessage}
          )
        );
    };

    const handleSearch = (groomedValues) => {
        if (pageNumber)
            setPageNumber(0);
        if (groomedValues.paymentFromDate)
            groomedValues.paymentFromDate = formatDate(moment(groomedValues.paymentFromDate).format());
        if (groomedValues.paymentToDate)
            groomedValues.paymentToDate = formatDate(moment(groomedValues.paymentToDate).format());
        groomedValues.limit = actualCriteria.limit;
        groomedValues.offset = 0;

        if (!isBillerRole) {
            if (groomedValues.searchBy === billerDetails[0].value)  // "billerId"
                delete groomedValues["billerName"];  // NOT sending billerName when searching by biller id
            else if (groomedValues.searchBy === billerDetails[1].value)  //  "billerName"
                delete groomedValues["bmoBillerId"]; // NOT sending bmoBillerId when searching by biller Name
        }
    
        setActualCriteria({...groomedValues});
        loadResults(groomedValues, " (search)");
    };

    const handleReset = () => {
        setSearchValues({...initialSearchValues});
        setActualCriteria({ ...initialSearchValues, offset: 0, limit: actualCriteria.limit});
        setPageNumber(0);
        setErrorMessage(null);

        dispatch({
          type: "GET_TRANSACTION_FOR_REJECT_SUCCESS",
          data: { data: null, transactionForRejectDetails: null },
        });
    };

    return (
        <div role="main">
            <ResponsiveBox ml={3}>
                <ERPSRecallRejectSearch 
                    pageType="RPM"
                    pageTitle={t("erps.rejectpaymentmerchants.page.title")}
                    pageDescription={t("erps.transactionEnquiry.page.description")}
                    searchValues={searchValues}
                    setSearchValues={setSearchValues}
                    dates={dates}
                    loading={loading}
                    errorMessage={errorMessage}
                    componentAccess={componentAccess}
                    handleReset={handleReset}
                    handleSearch={handleSearch}
                    financialInstitutionList={fiList}
                    subscribeEntityList={subscribeEntityList}
                />

                {transactionData !== null && transactionData !== undefined
                 && Array.isArray(transactionData) && transactionData.length > 0 ? (
                    <>
                        <ResponsiveBox className="manageUsers_table_visible">
                            <ResponsiveDataGrid
                                data={{
                                    rows: transactionData,
                                    columns: rejectTransactionsHeading(fiMap),
                                    count: rejectTransactionResponse?.transactionForRejectData?.count || 0
                                }}
                                sumOfColumnWidth="true"
                                initialLoading={loading}
                                pageNumber={pageNumber}
                                setPage={setPageNumber}
                                rowPerPage={actualCriteria.limit}
                                tableLabelText={t("erps.transactionEnquiry.page.title")}
                                hideFooterPagination={false}
                                pageName={"transaction-enquiry"}
                                onAnyPaginationAction={paginate}
                            />
                        </ResponsiveBox>
                        <ResponsiveBox className="manageUsers_card_visible">
                            <CardList
                                data={{
                                    rows: transactionData,
                                    columns: rejectTransactionsHeading(fiMap),
                                    count: rejectTransactionResponse?.transactionForRejectData?.count || 0
                                }}
                                onAnyPaginationAction={paginate}
                                hideFooterPagination={false}
                                pageName={"transaction-enquiry"}
                                pageNumber={pageNumber}
                                setPage={setPageNumber}
                                rowPerPage={actualCriteria.limit}
                            />
                        </ResponsiveBox>
                    </>
                ) : (
                    <ResponsiveBox mt={1}>
                        <Typography variant="body1">
                            { (transactionData?.length === 0)
                                ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                                : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                            }
                        </Typography>
                    </ResponsiveBox>
                )}
            </ResponsiveBox>
        </div>
    );

};

export default withSidebar(RejectTransactions);